/**
 * ThisDeviceOlderOrdersTab — older order ranges kept on this computer.
 *
 * TCA-26100804, step 3 of the tab-wise restructure. The status slot summarises
 * what is kept; the existing card below carries the request and retry flows
 * unchanged, because this step converts presentation and does not touch those
 * actions.
 *
 * The kept-range summary comes from the card, which already subscribes to that
 * status. Reading it again here would duplicate a subscription for no benefit —
 * the pattern recorded for later review in this TCA.
 */
import React, { useCallback, useState } from 'react';

import DeviceStatusSlot from './DeviceStatusSlot';
import HistoricalOfflineDataCard from './HistoricalOfflineDataCard';

export default function ThisDeviceOlderOrdersTab({ onSummary = null }) {
  const [summary, setSummary] = useState(null);

  const receive = useCallback((next) => {
    setSummary(next);
    if (onSummary) onSummary(next);
  }, [onSummary]);

  // Nothing is claimed until the status has actually been read: an unknown
  // count is not evidence that nothing is kept.
  const lines = [];
  let title = null;
  if (summary) {
    title = summary.rangeCount > 0
      ? `${summary.rangeCount} older date ${summary.rangeCount === 1 ? 'range is' : 'ranges are'} kept on this device`
      : 'No older date ranges are kept on this device';
    if (summary.rangeCount > 0) {
      lines.push(summary.oldest
        ? `${summary.orderCount.toLocaleString()} orders · oldest ${summary.oldest}`
        : `${summary.orderCount.toLocaleString()} orders`);
    }
  }

  return (
    <div className="space-y-3" data-testid="this-device-older-orders-tab">
      {summary && (
        <DeviceStatusSlot
          tone="quiet"
          title={title}
          lines={lines}
          testId="older-orders-slot"
        />
      )}
      <HistoricalOfflineDataCard onSummary={receive} />
    </div>
  );
}
